import React, { FC, useMemo } from "react";
import classNames from "classnames/dedupe";
import { StarWhiteIcon, StarBlueIcon } from "../../../icons";

export type TRatingSize = "sm" | "md" | "lg" | "2xl";

export interface IRatingProps {
  /** Basic classes to apply */
  classes?: {
    /** class for a wrapper */
    root?: string;
    /** class for basic stars */
    emptyState?: string;
    /** class for filled stars */
    filledState?: string;
  };
  /** Amount of visible stars */
  totalItems?: number;
  /** Fill stars value (from 0.0 to 5.0 or a custom totalItems count). */
  value?: number | string;
  /** Size of an item.
   * You can pass a custom font-size and stars will change their size accordingly.
   * */
  size?: TRatingSize;
  /** Size between stars (needed for correct width calculation of filled stars) */
  gutterSize?: string;
  /** Ability to change rating. Also ads c-pointer for items */
  editable?: boolean;
  /** A callback for rating change */
  onChange?(newRating: number): void;
}

const Rating: FC<IRatingProps> = ({
  classes,
  value = 0,
  totalItems = 5,
  gutterSize = "0.5em",
  size = "md",
  editable,
  onChange,
}) => {
  const itemsArr = Array.from(Array(totalItems).keys());
  const readonlyInfoStyles = useMemo(() => {
    const currentFill = Number(value) ? Number(value) / totalItems : 0;
    return {
      width: `calc(${currentFill} * 100% + ${gutterSize} * ${currentFill})`,
    };
  }, [gutterSize, totalItems, value]);
  return (
    <div
      className={classNames(
        "rating-scale",
        size ? `rating-scale--size-${size}` : "",
        classes?.root || "",
      )}
      data-testid="Rating__wrapper"
    >
      <div className="rating-scale__row position-relative flex-nowrap">
        {itemsArr.map((v) => (
          <div
            key={v}
            className={`rating-scale__col d-flex align-items-center justify-content-center ${
              editable ? "cursor-pointer" : ""
            }`}
            data-testid="Rating__mainStar"
            onClick={() => (editable ? onChange(Number(v) + 1) : "")}
          >
            <StarWhiteIcon className={classes?.emptyState || ""} />
          </div>
        ))}
      </div>
      <div
        className="rating-scale__blank rating-scale__row flex-nowrap"
        style={readonlyInfoStyles}
        data-testid="Rating__filledScale"
      >
        {itemsArr.map((v) => (
          <div
            key={v}
            className={`rating-scale__col d-flex align-items-center justify-content-center ${
              editable ? "cursor-pointer" : ""
            }`}
            data-testid="Rating__secondaryStar"
            onClick={() => (editable ? onChange(Number(v) + 1) : "")}
          >
            <StarBlueIcon className={classes?.filledState || ""} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Rating;
